import { Injectable } from '@angular/core';
import { UnitsStore } from '../../../@store';
import {
  convertToBar,
  convertToCubicFeet,
  convertToCubicMeter,
  convertToFahrenheitRounded,
  convertToGallonsPerMinute,
  convertToLitersPerMinute,
  convertToPoundPerSquareInch,
  getCelsiusFromFahrenheit,
} from '../../../features/temperature/converter/converter';
import { SUFFIX, Unit } from '../../../shared/common/units';
import { DecimalPipeWrapper } from '../../../shared/pipes/decimal-pipe-wrapper/decimal-pipe-wrapper.pipe';
import { UnitsAdaptee } from './units.model';

/**
 * unifies units to a standardized format,
 * takes decimal point into consideration,
 * takes bussiness specific unit transformation cases into consideration
 */
@Injectable()
export class UnitsAdapter {
  private isTemperatureMetricUnit: boolean;
  private isVolumeMetricUnit: boolean;
  private isPressureMetricUnit: boolean;
  private isFlowRateMetricUnit: boolean;

  constructor(
    private adaptee: UnitsAdaptee,
    private decimalPipeWrapper: DecimalPipeWrapper,
    private unitsStore: UnitsStore
  ) {
    this.unitsStore.isTemperatureMetric$.subscribe(
      (isMetric) => (this.isTemperatureMetricUnit = isMetric)
    );
    this.unitsStore.isVolumeMetricUnit$.subscribe(
      (isMetric) => (this.isVolumeMetricUnit = isMetric)
    );
    this.unitsStore.isPressureMetricUnit$.subscribe(
      (isMetric) => (this.isPressureMetricUnit = isMetric)
    );
    this.unitsStore.isFlowRateMetricUnit$.subscribe(
      (isMetric) => (this.isFlowRateMetricUnit = isMetric)
    );
  }

  // Transforms custom units into application-wide correct format, agnostic to metric/imperial system
  standardizeUnit(unit: Unit | string): string {
    return this.adaptee.standardizeUnit(unit);
  }

  /**
   * Transforms value into application-wide correct value format with respect to unit and metric/imperial system
   * Use for calculations when precision is important
   * Note: returns values like 209.00000006 due to high precision. Do not use for data displaying purpose.
   */
  getValue(value: number, unit: Unit | string): number {
    if (!value || !unit) {
      return value;
    }

    // first transformation is done to estimate stepping that should be actually applied during transformation
    const firstTransformationValue = this.convertToImperial(value, unit);

    // second transformation is the precise transformation with dynamic stepping
    const dynamicSteppingFromFirstTransformation = this.getDynamicStepping(
      firstTransformationValue
    );
    const secondTransformationValue = this.convertToImperial(
      value,
      unit,
      dynamicSteppingFromFirstTransformation
    );

    return secondTransformationValue;
  }

  /**
   * Transforms value into application-wide correct value format with respect to unit and metric/imperial system
   * 1. transforms value with respect to metic/imperial system
   * 2. does the rounding of value eg 12.100000006 -> 12.1
   * 3. does the localization eg en-GB 100,100.200 to de-DE 100.100,200
   */
  getDisplayValue(
    value: number,
    unit: Unit | string,
    config?: { precision?: number; digitsInfo?: string }
  ): string {
    const systemTransformedValue = this.getValue(value, unit);
    const localizedValue = this.decimalPipeWrapper.transform(
      systemTransformedValue,
      config?.digitsInfo
    );

    return localizedValue;
  }

  // Reverse transformation from imperial to metric unit
  getMetricValue(imperialValue: number, imperialUnit: Unit | string): number {
    let transformedValue = imperialValue;

    switch (imperialUnit) {
      case Unit.Fahrenheit:
        transformedValue = getCelsiusFromFahrenheit(imperialValue, 0);
        break;

      case Unit.GaloonHour:
        transformedValue = convertToLitersPerMinute(imperialValue, 60, 0);
        break;
      case Unit.GaloonMinute:
        transformedValue = convertToLitersPerMinute(imperialValue, 60, 0);
        break;
      case Unit.Galoon:
        transformedValue = convertToLitersPerMinute(imperialValue, 1, 0);
        break;
      case Unit.CubicFeetPerHour:
      case Unit.CubicFeet:
        transformedValue = convertToCubicMeter(imperialValue, 0);
        break;
      case Unit.DeciPoundPerSquareInch:
        const propertyInPsi = imperialValue * 10;

        transformedValue = convertToBar(propertyInPsi, 0);
        break;
      case Unit.PoundPerSquareInch:
        transformedValue = convertToBar(imperialValue, 0);
        break;
      default:
        transformedValue = imperialValue;
        break;
    }

    return transformedValue;
  }

  // Transforms unit with respect to imperial/metric system
  getUnit(unit: Unit | string): string {
    const _unit = this.adaptee.standardizeUnit(unit);

    switch (_unit) {
      case Unit.Decibar:
        return this.isPressureMetricUnit ? Unit.Decibar : Unit.DeciPoundPerSquareInch;
      case Unit.Bar:
        return this.isPressureMetricUnit ? Unit.Bar : Unit.PoundPerSquareInch;
      case Unit.Liter:
        return this.isFlowRateMetricUnit ? Unit.LiterHour : Unit.GaloonMinute;
      case Unit.GaloonMinute:
        return this.isFlowRateMetricUnit ? Unit.LiterHour : Unit.GaloonMinute;
      case Unit.LiterHour:
        return this.isFlowRateMetricUnit ? Unit.LiterHour : Unit.GaloonMinute;
      case Unit.Bar:
        return this.isPressureMetricUnit ? Unit.Bar : Unit.PoundPerSquareInch;
      case Unit.CubicMeter:
        return this.isVolumeMetricUnit ? Unit.CubicMeter : Unit.CubicFeet;
      case Unit.CubicMeterPerHour:
        return this.isVolumeMetricUnit ? Unit.CubicMeterPerHour : Unit.CubicFeetPerHour;
      case Unit.Celsius:
        return this.isTemperatureMetricUnit ? Unit.Celsius : Unit.Fahrenheit;
      case Unit.Percent:
        return Unit.Percent;
      default:
        return unit;
    }
  }

  // Transforms unit to a suffix with respect to imperial/metric system
  getSuffix(unit: Unit | string): SUFFIX | string {
    if (!unit) {
      return unit;
    }

    const standarizedUnit = this.adaptee.standardizeUnit(unit);
    const systemTranformedUnit = this.getUnit(standarizedUnit);

    switch (systemTranformedUnit) {
      case Unit.Decibar:
      case Unit.Bar:
        return SUFFIX.bar;
      case Unit.DeciPoundPerSquareInch:
      case Unit.PoundPerSquareInch:
        return SUFFIX.poundPerSquareInch;
      case Unit.Strength:
        return SUFFIX.hour;
      case Unit.Dbm:
        return SUFFIX.decibelMilliWatt;
      case Unit.Hour:
      case Unit.Hours:
        return SUFFIX.hour;
      case Unit.Liter:
        return SUFFIX.litersPerHour;
      case Unit.GaloonMinute:
        return SUFFIX.gallonPerMinute;
      case Unit.LiterHour:
        return SUFFIX.litersPerHour;
      case Unit.Minute:
        return SUFFIX.minute;
      case Unit.Kelvin:
        return SUFFIX.kelvin;
      case Unit.KelvinPerMinute:
        return SUFFIX.kelvin;
      case Unit.Fahrenheit:
        return SUFFIX.fahrenheit;
      case Unit.Celsius:
        return SUFFIX.celsius;
      case Unit.WattHour:
      case Unit.WattHours:
      case Unit.ShortWattHour:
        return SUFFIX.wattHour;
      case Unit.KilowattHour:
      case Unit.ShortKilowattHour:
        return SUFFIX.kilowattHour;
      case Unit.MegawattHour:
      case Unit.ShortMegawattHour:
        return SUFFIX.megawattHour;
      case Unit.KilowattPeak:
        return SUFFIX.kilowattPeak;
      case Unit.Watt:
      case Unit.ShortWatt:
        return SUFFIX.watt;
      case Unit.Kilowatt:
      case Unit.ShortKilowatt:
        return SUFFIX.kilowatt;
      case Unit.Megawatt:
      case Unit.ShortMegawatt:
        return SUFFIX.megawatt;
      case Unit.Voltage:
        return SUFFIX.voltage;
      case Unit.CubicMeter:
        return SUFFIX.cubicMeter;
      case Unit.CubicMeterPerHour:
        return SUFFIX.cubicMeterPerHour;
      case Unit.CubicFeet:
        return SUFFIX.cubicFeet;
      case Unit.CubicFeetPerHour:
        return SUFFIX.cubicFeetPerHour;
      case Unit.Kilogram:
        return SUFFIX.kilogram;
      case Unit.Ton:
        return SUFFIX.ton;
      case Unit.Kilometre:
        return SUFFIX.kilometre;
      case Unit.Percent:
        return SUFFIX.percentage;
      case Unit.Ampere:
        return SUFFIX.ampere;
      case Unit.AmpereHour:
        return SUFFIX.ampereHour;
      case Unit.MilliAmpere:
        return SUFFIX.milliAmpere;
      case Unit.PartsPerMillion:
        return SUFFIX.partsPerMillion;
      case Unit.RotationPerMinute:
        return SUFFIX.rotationPerMinute;
      case Unit.NoUnit:
        return SUFFIX.noUnit;
      case Unit.LiterMinute:
        return SUFFIX.litersPerMinute;
      case Unit.Rps:
      case Unit.RevolutionsPerSecond:
        return SUFFIX.rps;
      case Unit.Ohm:
        return SUFFIX.ohm;
      case Unit.kelvinMinute:
        return SUFFIX.kelvinMinute;
      default:
        return '';
    }
  }

  private convertToImperial(value: number, unit: string, stepping = 0): number {
    const _standardizedUnit = this.adaptee.standardizeUnit(unit);

    const systemTransformedUnit = this.getUnit(unit);

    let transformedValue;

    switch (systemTransformedUnit) {
      case Unit.Fahrenheit:
        transformedValue = convertToFahrenheitRounded(value, stepping);
        break;
      case Unit.GaloonMinute:
        if (_standardizedUnit === Unit.LiterHour) {
          transformedValue = convertToGallonsPerMinute(value, 60, stepping);
        } else {
          transformedValue = convertToGallonsPerMinute(value, 1, stepping);
        }
        break;
      case Unit.Galoon:
        transformedValue = convertToGallonsPerMinute(value, 1, stepping);
        break;
      case Unit.CubicFeetPerHour:
      case Unit.CubicFeet:
        transformedValue = convertToCubicFeet(value, stepping);
        break;
      case Unit.DeciPoundPerSquareInch:
        const propertyInBar = value / 10;

        transformedValue = convertToPoundPerSquareInch(propertyInBar, stepping);
        break;
      case Unit.Decibar: {
        const propertyInBar = value / 10;

        transformedValue = propertyInBar;
        break;
      }
      case Unit.PoundPerSquareInch: {
        transformedValue = convertToPoundPerSquareInch(value, stepping);
        break;
      }
      default: {
        transformedValue = value;
        break;
      }
    }

    return transformedValue;
  }

  private getDynamicStepping(value: number): number {
    if (value < 10) {
      return 0.1;
    } else {
      return 0;
    }
  }
}
